"use client";

import { useState } from "react";
import styled from "styled-components";
import { Meal } from "@/types/meal";
import Image from "next/image";
import { colors } from "@/styles/brand";

const Card = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  background: #f0f0f0;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const CarouselTrack = styled.div<{ $translateX: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${(props) => props.$translateX}%);
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  height: 100%;
  position: relative;
`;

const CarouselButton = styled.button<{ $direction: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${(props) =>
    props.$direction === "left" ? "left: 0.5rem" : "right: 0.5rem"};
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: #333;
  transition: all 0.3s;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const CarouselDot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) =>
    props.$active ? "#C8553D" : "rgba(255, 255, 255, 0.7)"};
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  &:hover {
    background: ${(props) =>
      props.$active ? "#C8553D" : "rgba(255, 255, 255, 0.9)"};
    transform: scale(1.3);
  }
`;

const MealImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvailableBadge = styled.div<{ $mealType?: string }>`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${(props) =>
    props.$mealType === "Pre-Made" ? colors.secondary : colors.success};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: ${(props) =>
    props.$mealType === "Pre-Made"
      ? "0 2px 8px rgba(242, 133, 59, 0.4)"
      : "0 2px 8px rgba(85, 139, 141, 0.4)"};
`;

const Content = styled.div`
  padding: 1.25rem;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProducerSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ProducerInfo = styled.div`
  flex: 1;
`;

const Label = styled.span`
  font-size: 0.75rem;
  color: #999;
  display: block;
  margin-bottom: 0.25rem;
`;

const ProducerName = styled.span`
  font-size: 0.9rem;
  color: #c8553d;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const RatingSection = styled.div`
  text-align: right;
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
`;

const Star = styled.span<{ $filled: boolean }>`
  color: ${(props) => (props.$filled ? "#F2853B" : "#e0e0e0")};
  font-size: 1rem;
`;

const ReviewCount = styled.span`
  font-size: 0.75rem;
  color: #999;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const Distance = styled.div`
  display: flex;
  flex-direction: column;
`;

const DistanceLabel = styled.span`
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 0.25rem;
`;

const DistanceValue = styled.span`
  font-size: 0.9rem;
  color: #333;
  font-weight: 600;
`;

const ViewButton = styled.button`
  background: #c8553d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #b04731;
  }
`;

const PriceTag = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 700;
`;

interface MealCardProps {
  meal: Meal;
  onClick?: () => void;
  onArtisanClick?: () => void;
}

export default function MealCard({
  meal,
  onClick,
  onArtisanClick,
}: MealCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Collect all valid images
  const images = [meal.image, meal.imageAlt, meal.imageAlt2].filter(
    (img): img is string => !!img,
  );

  const renderStars = () => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star key={i} $filled={i < meal.rating}>
          ★
        </Star>,
      );
    }
    return stars;
  };

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  return (
    <Card onClick={onClick}>
      <ImageContainer>
        {images.length === 1 ? (
          // Single image - no carousel
          <MealImage
            src={images[0] || "/assets/no-meal-image.png"}
            alt={meal.title}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        ) : (
          // Multiple images - show carousel
          <CarouselContainer>
            <CarouselTrack $translateX={-currentImageIndex * 100}>
              {images.map((image, index) => (
                <CarouselSlide key={index}>
                  <MealImage
                    src={image}
                    alt={`${meal.title} - Image ${index + 1}`}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </CarouselSlide>
              ))}
            </CarouselTrack>
            {images.length > 1 && (
              <>
                <CarouselButton $direction="left" onClick={goToPrevious}>
                  ‹
                </CarouselButton>
                <CarouselButton $direction="right" onClick={goToNext}>
                  ›
                </CarouselButton>
                <CarouselDots>
                  {images.map((_, index) => (
                    <CarouselDot
                      key={index}
                      $active={index === currentImageIndex}
                      onClick={(e) => goToSlide(e, index)}
                    />
                  ))}
                </CarouselDots>
              </>
            )}
          </CarouselContainer>
        )}
        {meal.available === 0 && (
          <AvailableBadge $mealType={meal.mealType}>
            {meal.mealType === "Pre-Made"
              ? "Available Now"
              : meal.mealType === "Made to Order"
                ? "Made to Order"
                : "Available Now"}
          </AvailableBadge>
        )}
        {meal.price && <PriceTag>${meal.price.toFixed(2)}</PriceTag>}
      </ImageContainer>

      <Content>
        <Title>{meal.title}</Title>

        <ProducerSection>
          <ProducerInfo>
            <Label>Sold by:</Label>
            <ProducerName
              onClick={(e) => {
                e.stopPropagation();
                onArtisanClick?.();
              }}
            >
              {meal.producer}
            </ProducerName>
          </ProducerInfo>

          <RatingSection>
            <Stars>{renderStars()}</Stars>
            <ReviewCount>
              {meal.ratingCount} {meal.ratingCount === 1 ? "review" : "reviews"}
            </ReviewCount>
          </RatingSection>
        </ProducerSection>

        {/* <Description>{truncateDescription(meal.description)}</Description> */}

        <Footer>
          {meal.distance !== undefined && (
            <Distance>
              <DistanceLabel>Distance to Kitchen</DistanceLabel>
              <DistanceValue>{meal.distance.toFixed(1)} miles</DistanceValue>
            </Distance>
          )}

          <ViewButton
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            View & Order
          </ViewButton>
        </Footer>
      </Content>
    </Card>
  );
}
